/**
 * High-performance opened trades store
 */

import { create } from "zustand";
import type {
  OpenedTradesResponse,
  OpenedTradesState,
  OpenedTradesActions,
  OpenedTrade,
  OrderModifyResponse,
  OrderProgress,
  OrderUpdate,
  VybeDealInternal,
} from "../types";
import { VybeOrderType, VybeUpdateType } from "../types";
import {
  getRequiredProfitConversionSymbols,
  getMarketPositionSides,
  isTradeDisplayRow,
  isMarketTrade,
  isTradeModifyUpdateType,
  isTradeRemovalUpdateType,
  isTradeUpsertUpdateType,
} from "../utils";

interface OpenedTradesStore extends OpenedTradesState, OpenedTradesActions {}

const initialState: OpenedTradesState = {
  byTicket: new Map(),
  bySymbol: new Map(),
  loadingStatus: "idle",
  errorMessage: null,
  lastUpdated: null,
  allTrades: [],
  totalCount: 0,
};

const sortTradesByOpenTime = (trades: OpenedTrade[]) =>
  [...trades].sort(
    (a, b) => new Date(b.openTime).getTime() - new Date(a.openTime).getTime()
  );

const rebuildTradesBySymbol = (trades: Iterable<OpenedTrade>) => {
  const bySymbol = new Map<string, OpenedTrade[]>();

  for (const trade of trades) {
    const symbolTrades = bySymbol.get(trade.symbol) || [];
    symbolTrades.push(trade);
    bySymbol.set(trade.symbol, symbolTrades);
  }

  return bySymbol;
};

const addPositiveTicket = (
  tickets: Set<number>,
  ticket: number | null | undefined
) => {
  if (typeof ticket === "number" && ticket > 0) {
    tickets.add(ticket);
  }
};

const getRemovalTicketsFromOrderUpdate = (
  data: OrderUpdate,
  includeOrderTicket: boolean
) => {
  const tickets = new Set<number>();

  if (includeOrderTicket) {
    addPositiveTicket(tickets, data.order?.ticket);
  }

  addPositiveTicket(tickets, data.closeByTicket);
  addPositiveTicket(tickets, data.deal?.positionTicket);
  addPositiveTicket(tickets, data.oppositeDeal?.positionTicket);
  addPositiveTicket(tickets, data.orderInternal?.ticketNumber);
  addPositiveTicket(tickets, data.orderInternal?.ticket);

  return tickets;
};

const reconcileCloseByRemainingOrder = (
  remainingOrder: OpenedTrade,
  data: OrderUpdate,
  previousByTicket: Map<number, OpenedTrade>
) => {
  if (data.type !== VybeUpdateType.MarketCloseBy) {
    return remainingOrder;
  }

  const existingTrade = previousByTicket.get(remainingOrder.ticket);
  const closedLots = data.deal?.lots ?? 0;

  if (!existingTrade || closedLots <= 0) {
    return remainingOrder;
  }

  const expectedLots = Number((existingTrade.lots - closedLots).toFixed(8));
  if (expectedLots <= remainingOrder.lots) {
    return remainingOrder;
  }

  const closedVolume = data.deal?.volume ?? 0;
  const expectedVolume =
    existingTrade.volume > closedVolume
      ? existingTrade.volume - closedVolume
      : remainingOrder.volume;

  return {
    ...remainingOrder,
    lots: expectedLots,
    volume: expectedVolume,
    dealInternalIn: remainingOrder.dealInternalIn
      ? {
          ...remainingOrder.dealInternalIn,
          lots: expectedLots,
          volume: expectedVolume,
        }
      : remainingOrder.dealInternalIn,
  };
};

const roundLots = (value: number) => Number(value.toFixed(8));

const isSameMarketSide = (left: OpenedTrade, right: OpenedTrade) =>
  left.orderType === right.orderType;

const isOppositeMarketSide = (left: OpenedTrade, right: OpenedTrade) =>
  (left.orderType === VybeOrderType.Buy &&
    right.orderType === VybeOrderType.Sell) ||
  (left.orderType === VybeOrderType.Sell &&
    right.orderType === VybeOrderType.Buy);

const withLotsAndVolume = (
  trade: OpenedTrade,
  lots: number,
  volume: number
): OpenedTrade => ({
  ...trade,
  lots,
  volume,
  dealInternalIn: trade.dealInternalIn
    ? {
        ...trade.dealInternalIn,
        lots,
        volume,
      }
    : trade.dealInternalIn,
});

const mergeSameDirectionNetPosition = (
  existingTrade: OpenedTrade,
  incomingTrade: OpenedTrade
): OpenedTrade => {
  const lots = roundLots(existingTrade.lots + incomingTrade.lots);
  const volume = existingTrade.volume + incomingTrade.volume;
  const openPrice =
    lots > 0
      ? ((existingTrade.openPrice * existingTrade.lots) +
          (incomingTrade.openPrice * incomingTrade.lots)) / lots
      : incomingTrade.openPrice;

  return withLotsAndVolume(
    {
      ...incomingTrade,
      ticket: existingTrade.ticket,
      openPrice,
      openTime: existingTrade.openTime,
      openTimestampUTC: existingTrade.openTimestampUTC,
      closePrice: 0,
      closeLots: 0,
      closeVolume: 0,
      closeComment: null,
      closeTimestampUTC: 0,
    },
    lots,
    volume
  );
};

const getMarketOrderTypeFromDeal = (deal: VybeDealInternal) => {
  return deal.type === 1 ? VybeOrderType.Sell : VybeOrderType.Buy;
};

const buildTradeFromNettingDeal = (
  sourceTrade: OpenedTrade,
  deal: VybeDealInternal
): OpenedTrade => {
  const ticket = deal.ticketNumber > 0 ? deal.ticketNumber : sourceTrade.ticket;
  const lots = roundLots(deal.lots);
  const volume = deal.volume;

  return withLotsAndVolume(
    {
      ...sourceTrade,
      ticket,
      symbol: deal.symbol || sourceTrade.symbol,
      orderType: getMarketOrderTypeFromDeal(deal),
      dealType: deal.type,
      openPrice: deal.openPrice,
      openTime: deal.openTimeAsDateTime || sourceTrade.openTime,
      openTimestampUTC: deal.openTimeMs || sourceTrade.openTimestampUTC,
      profit: deal.profit,
      swap: deal.swap,
      commission: deal.commission,
      fee: deal.fee,
      contractSize: deal.contractSize,
      expertId: deal.expertId,
      stopLoss: deal.stopLoss,
      takeProfit: deal.takeProfit,
      comment: deal.comment,
      digits: deal.digits,
      profitRate: deal.profitRate,
      closePrice: 0,
      closeLots: 0,
      closeVolume: 0,
      closeComment: null,
      closeTimestampUTC: 0,
      dealInternalIn: deal,
      dealInternalOut: null,
      orderInternal: null,
    },
    lots,
    volume
  );
};

const getTradeIdentity = (trade: OpenedTrade) =>
  `${trade.ticket}:${trade.symbol}:${trade.orderType}:${trade.openPrice}:${trade.comment ?? ""}`;

const getTradePositionTickets = (trade: OpenedTrade) => {
  const tickets = new Set<number>();
  addPositiveTicket(tickets, trade.ticket);
  addPositiveTicket(tickets, trade.dealInternalIn?.ticketNumber);
  addPositiveTicket(tickets, trade.dealInternalIn?.positionTicket);
  return tickets;
};

const getOrderUpdatePositionTickets = (
  trade: OpenedTrade,
  data?: OrderUpdate
) => {
  const tickets = getTradePositionTickets(trade);
  addPositiveTicket(tickets, data?.deal?.ticketNumber);
  addPositiveTicket(tickets, data?.deal?.positionTicket);
  addPositiveTicket(tickets, data?.oppositeDeal?.ticketNumber);
  addPositiveTicket(tickets, data?.oppositeDeal?.positionTicket);
  return tickets;
};

const sharesAnyTicket = (
  left: Set<number>,
  right: Set<number>
) => {
  for (const ticket of left) {
    if (right.has(ticket)) {
      return true;
    }
  }

  return false;
};

const rebuildTradesByTicket = (trades: Iterable<OpenedTrade>) => {
  const byTicket = new Map<number, OpenedTrade>();

  for (const trade of trades) {
    byTicket.set(trade.ticket, trade);
  }

  return byTicket;
};

const removeTradesByTicket = (
  trades: OpenedTrade[],
  ticketsToRemove: Set<number>
) =>
  ticketsToRemove.size === 0
    ? trades
    : trades.filter((trade) => !ticketsToRemove.has(trade.ticket));

const upsertTradeByIdentity = (
  trades: OpenedTrade[],
  trade: OpenedTrade
) => {
  const identity = getTradeIdentity(trade);
  const existingIndex = trades.findIndex(
    (candidate) => getTradeIdentity(candidate) === identity
  );

  if (existingIndex < 0) {
    return [...trades, trade];
  }

  const nextTrades = [...trades];
  nextTrades[existingIndex] = trade;
  return nextTrades;
};

const upsertTradeByTicket = (
  trades: OpenedTrade[],
  trade: OpenedTrade
) => {
  const existingIndex = trades.findIndex(
    (candidate) => candidate.ticket === trade.ticket
  );

  if (existingIndex < 0) {
    return [...trades, trade];
  }

  // Modify events are authoritative for a position ticket. Collapse stale
  // same-ticket rows so TP/SL updates cannot render duplicate positions.
  const nextTrades = trades.filter(
    (candidate) => candidate.ticket !== trade.ticket
  );
  nextTrades.splice(Math.min(existingIndex, nextTrades.length), 0, trade);
  return nextTrades;
};

const upsertNettingMarketUpdate = (
  trades: OpenedTrade[],
  incomingTrade: OpenedTrade,
  data?: OrderUpdate
) => {
  if (!isMarketTrade(incomingTrade)) {
    return upsertTradeByIdentity(trades, incomingTrade);
  }

  const updatePositionTickets = getOrderUpdatePositionTickets(incomingTrade, data);
  const oppositeSideIndex = trades.findIndex(
    (trade) => {
      if (
        trade.symbol !== incomingTrade.symbol ||
        !isMarketTrade(trade) ||
        !isOppositeMarketSide(trade, incomingTrade)
      ) {
        return false;
      }

      return sharesAnyTicket(getTradePositionTickets(trade), updatePositionTickets);
    }
  );

  if (oppositeSideIndex >= 0) {
    const existingTrade = trades[oppositeSideIndex];
    const otherTrades = trades.filter((trade, index) => index !== oppositeSideIndex);

    if (data?.oppositeDeal && data.oppositeDeal.lots > 0) {
      return [
        ...otherTrades,
        buildTradeFromNettingDeal(incomingTrade, data.oppositeDeal),
      ];
    }

    if (existingTrade.lots > incomingTrade.lots) {
      const lots = roundLots(existingTrade.lots - incomingTrade.lots);
      const volume = Math.max(0, existingTrade.volume - incomingTrade.volume);
      return [...otherTrades, withLotsAndVolume(existingTrade, lots, volume)];
    }

    if (incomingTrade.lots > existingTrade.lots) {
      const lots = roundLots(incomingTrade.lots - existingTrade.lots);
      const volume = Math.max(0, incomingTrade.volume - existingTrade.volume);
      const reversedTrade = withLotsAndVolume(incomingTrade, lots, volume);
      return [...otherTrades, reversedTrade];
    }

    return otherTrades;
  }

  const sameSideIndex = trades.findIndex(
    (trade) => {
      if (
        trade.symbol !== incomingTrade.symbol ||
        !isMarketTrade(trade) ||
        !isSameMarketSide(trade, incomingTrade)
      ) {
        return false;
      }

      return sharesAnyTicket(getTradePositionTickets(trade), updatePositionTickets);
    }
  );

  if (sameSideIndex < 0) {
    return upsertTradeByIdentity(trades, incomingTrade);
  }

  const nextTrades = [...trades];
  nextTrades[sameSideIndex] = mergeSameDirectionNetPosition(
    nextTrades[sameSideIndex],
    incomingTrade
  );
  return nextTrades;
};

const applyOrderModifyResponseToTrades = (
  trades: OpenedTrade[],
  data: OrderModifyResponse
) => {
  if (!data.success) {
    return trades;
  }

  if (data.order && isTradeDisplayRow(data.order)) {
    return upsertTradeByTicket(trades, data.order);
  }

  const ticket = data.ticket;
  if (typeof ticket !== "number" || ticket <= 0) {
    return trades;
  }

  let tradeIndex = trades.findIndex((trade) => trade.ticket === ticket);

  if (tradeIndex < 0 && data.symbol) {
    const symbolMarketTrades = trades
      .map((trade, index) => ({ trade, index }))
      .filter(({ trade }) => trade.symbol === data.symbol && isMarketTrade(trade));

    if (symbolMarketTrades.length === 1) {
      tradeIndex = symbolMarketTrades[0].index;
    }
  }

  if (tradeIndex < 0) {
    return trades;
  }

  const existingTrade = trades[tradeIndex];
  const stopLoss =
    typeof data.newStopLoss === "number"
      ? data.newStopLoss
      : existingTrade.stopLoss;
  const takeProfit =
    typeof data.newTakeProfit === "number"
      ? data.newTakeProfit
      : existingTrade.takeProfit;
  const nextTrade: OpenedTrade = {
    ...existingTrade,
    stopLoss,
    takeProfit,
    dealInternalIn: existingTrade.dealInternalIn
      ? {
          ...existingTrade.dealInternalIn,
          stopLoss,
          takeProfit,
        }
      : existingTrade.dealInternalIn,
    orderInternal: existingTrade.orderInternal
      ? {
          ...existingTrade.orderInternal,
          stopLoss,
          takeProfit,
        }
      : existingTrade.orderInternal,
  };
  const nextTrades = [...trades];
  nextTrades[tradeIndex] = nextTrade;

  return nextTrades;
};

let pendingTradeSideEffects:
  | {
      trades: OpenedTrade[];
      affectedSymbols: Set<string>;
      logPrefix: string;
    }
  | null = null;
let tradeSideEffectsTimer: ReturnType<typeof setTimeout> | null = null;

const scheduleTradeSideEffects = (
  trades: OpenedTrade[],
  affectedSymbols: Set<string>,
  logPrefix: string
) => {
  pendingTradeSideEffects = {
    trades,
    affectedSymbols: new Set([
      ...(pendingTradeSideEffects?.affectedSymbols ?? []),
      ...affectedSymbols,
    ]),
    logPrefix,
  };

  if (tradeSideEffectsTimer) {
    return;
  }

  tradeSideEffectsTimer = setTimeout(() => {
    const pending = pendingTradeSideEffects;
    pendingTradeSideEffects = null;
    tradeSideEffectsTimer = null;

    if (!pending) {
      return;
    }

    flushTradeSideEffects(pending.trades, pending.affectedSymbols, pending.logPrefix);
  }, 0);
};

const flushTradeSideEffects = (
  trades: OpenedTrade[],
  affectedSymbols: Set<string>,
  logPrefix: string
) => {
  Promise.all([
    import("../../account-information/stores/useAccountInformationStore"),
    import("../../symbols/stores/useSymbolsStore"),
    import("../../subscribed-symbols/stores/useSubscribedSymbolsStore"),
    import("../../../lib/quotes-watchlist/store"),
  ])
    .then(
      ([
        { useAccountInformationStore },
        { useSymbolsStore },
        { useSubscribedSymbolsStore },
        { useQuotesWatchlistStore },
      ]) => {
        const tradesBySymbol = rebuildTradesBySymbol(trades);
        const symbolsStore = useSymbolsStore.getState();
        const watchlistStore = useQuotesWatchlistStore.getState();

        affectedSymbols.forEach((currency) => {
          const symbol = symbolsStore.getSymbolByCurrency(currency);
          if (!symbol) {
            return;
          }

          const { hasBuy, hasSell } = getMarketPositionSides(
            tradesBySymbol.get(currency) || []
          );
          watchlistStore.handleTradeSymbol(symbol, hasBuy, hasSell);
        });

        const account = useAccountInformationStore.getState().account;
        const accountCurrency =
          account?.currency || account?.symbolBaseInfo?.currency || "";
        if (!accountCurrency) return;

        const subscribedStore = useSubscribedSymbolsStore.getState();
        const requiredSymbols = new Set<string>();

        trades.forEach((trade) => {
          if (!isMarketTrade(trade)) return;

          const tradeSymbol = symbolsStore.getSymbolByCurrency(trade.symbol);
          if (!tradeSymbol) return;

          getRequiredProfitConversionSymbols(
            tradeSymbol.profitCurrency,
            accountCurrency,
            symbolsStore.byCurrency
          ).forEach((symbolName) => requiredSymbols.add(symbolName));
        });

        requiredSymbols.forEach((symbolName) => {
          const conversionSymbol = symbolsStore.getSymbolByCurrency(symbolName);
          if (conversionSymbol) {
            watchlistStore.addToWatchlist(conversionSymbol);
            if (!subscribedStore.isSubscribed(symbolName)) {
              subscribedStore.subscribe(conversionSymbol);
            }
          }
        });
      }
    )
    .catch((error) => {
      console.error(`${logPrefix} Failed to sync trade side effects:`, error);
    });
};

export const useOpenedTradesStore = create<OpenedTradesStore>((set, get) => {
  // Create stable function references
  const getTradeByTicket = (ticket: number) => get().byTicket.get(ticket);
  const getTradesBySymbol = (symbol: string) =>
    get().bySymbol.get(symbol) || [];

  return {
    ...initialState,

    processOpenedTrades: (data: OpenedTradesResponse) => {
      set((state) => {
        if (!data.success) {
          return {
            ...state,
            loadingStatus: "error",
            errorMessage: data.errorMessage,
          };
        }

        // Build new Maps for high-performance lookups
        const newByTicket = new Map<number, OpenedTrade>();
        const newBySymbol = new Map<string, OpenedTrade[]>();

        // Process all trades
        data.orders.forEach((trade) => {
          // Add to ticket lookup
          newByTicket.set(trade.ticket, trade);

          // Group by symbol
          if (!newBySymbol.has(trade.symbol)) {
            newBySymbol.set(trade.symbol, []);
          }
          newBySymbol.get(trade.symbol)!.push(trade);
        });

        // Sort trades by open time (newest first)
        const sortedTrades = sortTradesByOpenTime(data.orders);

        const affectedSymbols = new Set([
          ...state.bySymbol.keys(),
          ...newBySymbol.keys(),
        ]);
        scheduleTradeSideEffects(sortedTrades, affectedSymbols, "[OpenedTrades]");

        return {
          ...state,
          byTicket: newByTicket,
          bySymbol: newBySymbol,
          allTrades: sortedTrades,
          totalCount: data.totalCount,
          loadingStatus: "complete",
          errorMessage: null,
          lastUpdated: new Date(),
        };
      });
    },

    processOrderProgress: (data: OrderProgress) => {
      set((state) => {
        // Log execution progress for debugging
        const statusName =
          data.tradeResult.status === 0x2719
            ? "REQUEST_EXECUTED"
            : data.tradeResult.status === 0x2712
            ? "REQUEST_ACCEPTED"
            : data.tradeResult.status === 0x2718
            ? "ORDER_PLACED"
            : `Status_${data.tradeResult.status}`;

        // For now, just log the progress - the actual trade will be added via VybeOnOrderUpdate
        // This gives us visibility into the execution flow
        return {
          ...state,
          lastUpdated: new Date(),
        };
      });
    },

    processOrderUpdate: (data: OrderUpdate) => {
      set((state) => {
        const trade = data.order;

        // A REJECTION is not a close.
        //
        // Rejected sits in the removal set because rejecting a pending ORDER should take its row away. But
        // the same update type arrives when the engine refuses a CLOSE request against an open position, and
        // removing the row there deletes a position that is still very much open. SR Global hit this by
        // spamming close-all: some closes were refused, each refusal erased its row, the screen showed zero
        // positions — and a reload brought them all back, because the engine had never closed them.
        //
        // Falling through leaves the position exactly as it was: an open position passes isTradeDisplayRow,
        // and Rejected is in neither the upsert nor the modify set, so nothing is written.
        const isRejectedOpenPosition =
          data.type === VybeUpdateType.Rejected &&
          trade !== null &&
          trade !== undefined &&
          isMarketTrade(trade);

        if (isTradeRemovalUpdateType(data.type) && !isRejectedOpenPosition) {
          const previousSymbols = new Set(state.bySymbol.keys());
          const remainingOrder = data.remainingOrder;
          const keepUpdatedTrade =
            !remainingOrder &&
            (data.type === VybeUpdateType.PartialClose ||
              data.type === VybeUpdateType.MarketCloseBy) &&
            trade !== null &&
            isTradeDisplayRow(trade);
          const ticketsToRemove = getRemovalTicketsFromOrderUpdate(
            data,
            !keepUpdatedTrade
          );
          let nextTrades = removeTradesByTicket(state.allTrades, ticketsToRemove);

          if (trade && keepUpdatedTrade) {
            nextTrades = upsertTradeByIdentity(nextTrades, trade);
          }

          if (remainingOrder && isTradeDisplayRow(remainingOrder)) {
            const reconciledRemainingOrder = reconcileCloseByRemainingOrder(
              remainingOrder,
              data,
              state.byTicket
            );
            nextTrades = upsertTradeByIdentity(nextTrades, reconciledRemainingOrder);
          }

          const newByTicket = rebuildTradesByTicket(nextTrades);
          const newBySymbol = rebuildTradesBySymbol(nextTrades);
          const affectedSymbols = new Set([
            ...previousSymbols,
            ...newBySymbol.keys(),
          ]);
          scheduleTradeSideEffects(nextTrades, affectedSymbols, "[OrderUpdate]");

          return {
            ...state,
            byTicket: newByTicket,
            bySymbol: newBySymbol,
            allTrades: sortTradesByOpenTime(nextTrades),
            totalCount: nextTrades.length,
            lastUpdated: new Date(),
          };
        }

        if (trade && !isTradeDisplayRow(trade)) {
          let nextTrades = state.allTrades;

          if (!state.byTicket.has(trade.ticket)) {
            return {
              ...state,
              lastUpdated: new Date(),
            };
          }

          const previousSymbols = new Set(state.bySymbol.keys());
          nextTrades = nextTrades.filter(
            (existingTrade) =>
              getTradeIdentity(existingTrade) !== getTradeIdentity(trade) &&
              existingTrade.ticket !== trade.ticket
          );
          const newByTicket = rebuildTradesByTicket(nextTrades);
          const newBySymbol = rebuildTradesBySymbol(nextTrades);
          const affectedSymbols = new Set([
            ...previousSymbols,
            ...newBySymbol.keys(),
          ]);
          scheduleTradeSideEffects(nextTrades, affectedSymbols, "[OrderUpdate]");

          return {
            ...state,
            byTicket: newByTicket,
            bySymbol: newBySymbol,
            allTrades: sortTradesByOpenTime(nextTrades),
            totalCount: nextTrades.length,
            lastUpdated: new Date(),
          };
        }

        if (
          trade &&
          (isTradeUpsertUpdateType(data.type) ||
            isTradeModifyUpdateType(data.type))
        ) {
          const previousSymbols = new Set(state.bySymbol.keys());
          const existingTrade = state.byTicket.get(trade.ticket);
          const nextTrades = isTradeUpsertUpdateType(data.type)
            ? upsertNettingMarketUpdate(state.allTrades, trade, data)
            : upsertTradeByTicket(state.allTrades, trade);
          const newByTicket = rebuildTradesByTicket(nextTrades);
          const newBySymbol = rebuildTradesBySymbol(nextTrades);
          const affectedSymbols = new Set([
            ...previousSymbols,
            ...newBySymbol.keys(),
          ]);

          if (existingTrade?.symbol) {
            affectedSymbols.add(existingTrade.symbol);
          }

          scheduleTradeSideEffects(nextTrades, affectedSymbols, "[OrderUpdate]");

          return {
            ...state,
            byTicket: newByTicket,
            bySymbol: newBySymbol,
            allTrades: sortTradesByOpenTime(nextTrades),
            totalCount: nextTrades.length,
            lastUpdated: new Date(),
          };
        }

        // Log other update types for debugging
        return {
          ...state,
          lastUpdated: new Date(),
        };
      });
    },

    processOrderModify: (data: OrderModifyResponse) => {
      set((state) => {
        const previousSymbols = new Set(state.bySymbol.keys());
        const nextTrades = applyOrderModifyResponseToTrades(state.allTrades, data);

        if (nextTrades === state.allTrades) {
          return {
            ...state,
            lastUpdated: new Date(),
          };
        }

        const newByTicket = rebuildTradesByTicket(nextTrades);
        const newBySymbol = rebuildTradesBySymbol(nextTrades);
        const affectedSymbols = new Set([
          ...previousSymbols,
          ...newBySymbol.keys(),
        ]);
        scheduleTradeSideEffects(nextTrades, affectedSymbols, "[OrderModify]");

        return {
          ...state,
          byTicket: newByTicket,
          bySymbol: newBySymbol,
          allTrades: sortTradesByOpenTime(nextTrades),
          totalCount: nextTrades.length,
          lastUpdated: new Date(),
        };
      });
    },

    reset: () => set(initialState),

    // Stable function references
    getTradeByTicket,
    getTradesBySymbol,
  };
});
